import { OutstandingAgreementType } from '__generated__/GQL';
import assertCustomGuardValue from 'common/bento/lib/assertCustomGuardValue';
import getIsAgreementCompleted from 'common/bento/modules/Agreement/libs/getIsAgreementCompleted';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import getCompanyComplianceModuleStatus, {
  CompanyComplianceModuleStatus,
} from 'features/Onboarding/modules/CompanyCompliance/libs/getCompanyComplianceModuleStatus';
import getComplianceStatus from 'features/Onboarding/modules/Compliance/libs/getComplianceStatus';
import { VerificationStatus } from 'features/Onboarding/modules/Verification/lib/steps/types';
import { ApplicantType } from 'features/Onboarding/types';

import { Model } from './model';

export enum Guard {
  IsAwaitingAccountValidation = 'isAwaitingAccountValidation',
  IsApplicationProfileComplete = 'isApplicationProfileComplete',
  ShouldFillLandbot = 'shouldFillLandbot',
  ShouldFillCompanyComplianceQuestions = 'shouldFillCompanyComplianceQuestions',
  HasCompletedAgreementOfType = 'hasCompletedAgreementOfType',
  IsCapitalDepositPaid = 'isCapitalDepositPaid',
  ShouldFillPersonalCompliance = 'shouldFillPersonalCompliance',
  CanSkipDataProcessingWait = 'canSkipDataProcessingWait',
}

const guards: MachineGuards<Model, Guard> = {
  canSkipDataProcessingWait: ({ getModuleData }) => {
    return !!getModuleData().viewer.company?.profile.legalName;
  },

  hasCompletedAgreementOfType: ({ getModuleData }, event, { cond }) => {
    assertCustomGuardValue<OutstandingAgreementType>(cond);

    const { outstandingAgreements } = getModuleData().viewer;

    if (!outstandingAgreements) {
      throw new Error(
        `outstandingAgreements required in 'hasCompletedAgreementOfType'`,
      );
    }

    return getIsAgreementCompleted({
      agreementType: cond.value,
      outstandingAgreements,
    });
  },

  isApplicationProfileComplete: ({ getModuleData }) =>
    Boolean(getModuleData().viewer.companyUser?.hasFinishedApplicationSetup),

  isAwaitingAccountValidation: ({ getModuleData }) =>
    Boolean(getModuleData().viewer.companyUser?.isAwaitingAccountValidation),

  isCapitalDepositPaid: ({ getModuleData }) => {
    return Boolean(getModuleData().viewer.company?.capitalDeposit?.isPaid);
  },

  shouldFillCompanyComplianceQuestions: ({ getModuleData }) => {
    const { shouldAskSanctionsDeclarationQuestions, viewer } = getModuleData();

    const capitalDeposit = viewer.company?.capitalDeposit;

    if (!viewer.company || !capitalDeposit) {
      return false;
    }

    const companyComplianceModuleStatus = getCompanyComplianceModuleStatus({
      companyProfile: viewer.company.profile,
      shouldAskSanctionsDeclarationQuestions,
    });

    return [
      CompanyComplianceModuleStatus.ToDo,
      CompanyComplianceModuleStatus.Refused,
    ].includes(companyComplianceModuleStatus);
  },

  shouldFillLandbot: ({ getModuleData }) => {
    const { company } = getModuleData().viewer;

    return !company.profile.legalName;
  },

  shouldFillPersonalCompliance: ({ getModuleData }) => {
    const userProfile = getModuleData().viewer.profile;

    return (
      getComplianceStatus({
        applicantType: ApplicantType.AccountHolder,
        userProfile,
      }) === VerificationStatus.ToDo
    );
  },
};

export default guards;
